import {
  getDashboardStatsOptions,
  listExperimentMetricsSummaryOptions,
  listWarehouseMetricsOptions,
} from "@/api/generated/@tanstack/react-query.gen";
import type {
  ContrastRow,
  ExperimentMetadata,
  ExperimentMetricsResponse,
  MetricSpecOutput,
} from "@/api/generated/types.gen";
import { cellStyles, createColumns, DataGrid } from "@/components/composite/data-grid";
import { DateRangePicker, type DateTimeRange } from "@/components/composite/date-range-picker";
import { MetricLiftCell } from "@/components/contrast/metric-lift-cell";
import { Page } from "@/components/misc";
import { Badge, ButtonGroup, FieldLabelInfoTip, Skeleton, Tooltip } from "@/components/ui";
import { useGridClient } from "@/lib/hooks/use-grid";
import { cn } from "@/lib/utils/cn";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { format, parseISO, subDays } from "date-fns";
import {
  FileEditIcon,
  FilterIcon,
  FlaskConicalIcon,
  GitBranchIcon,
  TrendingDownIcon,
  TrendingUpIcon,
} from "lucide-react";
import React from "react";

const DashboardSection: React.FC<{
  title: string;
  tooltip?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, tooltip, action, children }) => (
  <section className="space-y-3">
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-1.5">
        <h2 className="text-base font-semibold">{title}</h2>
        {tooltip && <FieldLabelInfoTip text={tooltip} />}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const StatCard: React.FC<{
  label: string;
  value: number | undefined;
  icon: React.ElementType;
  href: string;
  loading?: boolean;
}> = ({ label, value, icon: Icon, href, loading }) => (
  <Link
    to={href}
    className="rounded-lg border bg-card p-6 cursor-pointer transition-colors hover:border-primary/50"
  >
    <div className="flex items-center gap-3">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted">
        <Icon className="size-5 text-muted-foreground" />
      </div>
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-2xl font-semibold">
          {loading
            ? <Skeleton className="h-7 w-8" />
            : (value ?? 0)}
        </p>
      </div>
    </div>
  </Link>
);

const toISODate = (d: Date): string => format(d, "yyyy-MM-dd");

const formatCount = (n: number | null | undefined): string =>
  n == null ? "—" : new Intl.NumberFormat("en-US").format(n);

// Shared type styles for the metric-spec header tooltip — small-caps labels,
// tabular-nums values. (Cell-tooltip styling lives in `MetricLiftCell`.)
const LABEL_CLS = "text-muted-foreground text-[10px] uppercase tracking-wide";
const VALUE_CLS = "tabular-nums text-foreground";

const TooltipCardTitle: React.FC<{
  metricId: string;
  direction?: "increase" | "decrease" | null | undefined;
}> = ({ metricId, direction }) => (
  <div className="px-3 pt-2 pb-1.5 border-b border-border">
    <div className="flex items-center gap-1.5 text-[12px] font-semibold leading-tight text-foreground">
      <span className="font-mono break-all">{metricId}</span>
      <DirectionArrow direction={direction} />
    </div>
  </div>
);

const PHASE_VARIANT: Record<"explore" | "exploit", React.ComponentProps<typeof Badge>["variant"]> =
  {
    explore: "amber",
    exploit: "default",
  };

// Module-level empty summary so `summariesQuery.data ?? EMPTY_SUMMARY` doesn't
// mint a new `{ metric_ids: [], rows: [] }` on every render while the query is
// loading / refetching — that identity churn cascades into `columns` rebuilds
// and destabilizes TanStack Table.
const EMPTY_SUMMARY: ExperimentMetricsResponse = {
  start_date: "",
  end_date: "",
  metric_ids: [],
  params: { alpha: 0.10, power: 0.8, max_mde: 0.5 },
  rows: [],
  experiments: {},
};

// One row per configured experiment — the server already returns exactly its
// primary-metric contrast, so this is a 1:1 view over the `ContrastRow` list.
// `phase` is derived from the cohort labels on the row
// (the wire format only carries cohort ids, not the dashboard-config phase).
// `name` comes from the response's `experiments` map (sourced server-side
// from `ExperimentStore`); falls back to `experiment_id`. `n` is `null` when
// the experiment has no data in the window (both arms empty) → renders as "—".
type ExperimentMetricRow = {
  readonly experiment_id: string;
  readonly name: string;
  readonly metric_id: string;
  readonly n: number | null;
  readonly contrast: ContrastRow;
  readonly phase: "exploit" | "explore" | null;
  // ISO date (YYYY-MM-DD) of the experiment's exposure start, from config.
  readonly exposure_start: string | null;
};

const phaseFromTreatmentLabel = (label: string): "exploit" | "explore" | null => {
  if (label === "model") return "exploit";
  if (label === "uniform" || label === "uniform_other") return "explore";
  return null;
};

const buildExperimentMetricRows = (
  rows: readonly ContrastRow[],
  experiments: Readonly<Record<string, ExperimentMetadata>>,
): ExperimentMetricRow[] =>
  rows
    .map((r) => {
      const empty = r.treatment.stats.empty && r.control.stats.empty;
      return {
        experiment_id: r.experiment_id,
        name: experiments[r.experiment_id]?.name || r.experiment_id,
        metric_id: r.metric_id,
        n: empty ? null : (r.treatment.stats.n ?? 0) + (r.control.stats.n ?? 0),
        contrast: r,
        phase: phaseFromTreatmentLabel(r.treatment.label),
        exposure_start: r.exposure_start ?? null,
      };
    })
    .sort(
      (a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" })
        || a.metric_id.localeCompare(b.metric_id),
    );

// ─────────────────────────────────────────────────────────────────────────────
// Metric spec tooltip — compact reference card shown on header hover.
// ─────────────────────────────────────────────────────────────────────────────

const AGGREGATION_LABEL: Record<string, string> = {
  count: "count",
  sum: "sum",
  mean: "mean",
  count_distinct: "count distinct",
  max: "max",
  min: "min",
  first_value: "first value",
  latest_value: "latest value",
  daily_participation: "daily participation",
  unit_count: "unit count",
  ratio: "ratio",
};

// Single flag chip — compact, one-line, tinted per semantic.
const FlagChip: React.FC<{
  icon?: React.ElementType;
  label: string;
  tone: "indigo" | "amber" | "violet" | "slate";
}> = ({ icon: Icon, label, tone }) => {
  const tones = {
    indigo:
      "bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-950/50 dark:text-indigo-300 dark:border-indigo-900",
    amber:
      "bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/50 dark:text-amber-200 dark:border-amber-900",
    violet:
      "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/50 dark:text-violet-300 dark:border-violet-900",
    slate: "bg-muted text-muted-foreground border-border",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded border px-1.5 py-px text-[9px] font-semibold uppercase tracking-wider leading-none",
        tones[tone],
      )}
    >
      {Icon && <Icon className="size-2.5" strokeWidth={2.2} />}
      {label}
    </span>
  );
};

// Inline trending arrow — rendered next to the metric name. Color encodes
// which direction is "good" (emerald for both, since it marks intent).
const DirectionArrow: React.FC<{ direction: "increase" | "decrease" | null | undefined; }> = ({
  direction,
}) => {
  if (!direction) return null;
  const Icon = direction === "increase" ? TrendingUpIcon : TrendingDownIcon;
  return (
    <Icon
      className="size-3 text-emerald-600 dark:text-emerald-400 shrink-0"
      strokeWidth={2.5}
      aria-label={direction === "increase" ? "higher is better" : "lower is better"}
    />
  );
};

const MetricSpecTooltip: React.FC<{ metricId: string; spec: MetricSpecOutput | undefined; }> = ({
  metricId,
  spec,
}) => {
  if (!spec) {
    return (
      <div className="font-mono text-xs">
        <TooltipCardTitle metricId={metricId} />
        <div className="px-3 py-2 text-muted-foreground text-[11px]">
          Not in warehouse registry.
        </div>
      </div>
    );
  }

  const agg = spec.aggregation;
  const aggType = agg.type ?? "count";
  const sourceTable = "source_table_id" in agg ? agg.source_table_id : null;
  const directOnly = "only_direct_attribution" in agg ? agg.only_direct_attribution : false;
  const hasFilter = "criteria" in agg && agg.criteria != null;
  const rollupStart = "custom_rollup_start" in agg ? agg.custom_rollup_start : null;
  const rollupEnd = "custom_rollup_end" in agg ? agg.custom_rollup_end : null;
  const hasRollup = rollupStart != null || rollupEnd != null;
  const rollupLabel = hasRollup ? `d${rollupStart ?? 0}–d${rollupEnd ?? "∞"}` : null;

  const flags: Array<React.ReactElement> = [];
  if (spec.wait_for_cohort_window) {
    flags.push(<FlagChip key="cohorted" label="cohorted" tone="indigo" />);
  }
  if (directOnly) {
    flags.push(<FlagChip key="direct" label="direct only" tone="amber" />);
  }
  if (hasFilter) {
    flags.push(<FlagChip key="filtered" icon={FilterIcon} label="filtered" tone="violet" />);
  }
  if (rollupLabel) {
    flags.push(<FlagChip key="rollup" label={rollupLabel} tone="slate" />);
  }

  return (
    <div className="font-mono text-xs max-w-[20rem]">
      <TooltipCardTitle metricId={metricId} direction={spec.direction} />

      <div className="px-3 py-2 space-y-2">
        {spec.description && (
          <p className="text-[11px] leading-snug text-muted-foreground">{spec.description}</p>
        )}

        {/* Facet grid — label/value rows, matching the cell tooltip typography */}
        <div className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-0.5">
          <span className={LABEL_CLS}>agg</span>
          <span className={VALUE_CLS}>{AGGREGATION_LABEL[aggType] ?? aggType}</span>
          <span className={LABEL_CLS}>unit</span>
          <span className={VALUE_CLS}>{spec.unit_id_type_id}</span>
          {sourceTable != null && (
            <>
              <span className={LABEL_CLS}>source</span>
              <span className={cn(VALUE_CLS, "break-all")}>{sourceTable}</span>
            </>
          )}
        </div>

        {/* Flag chip rail — only shown if at least one flag applies */}
        {flags.length > 0 && (
          <>
            <div className="h-px bg-border" />
            <div className="flex flex-wrap gap-1">{flags}</div>
          </>
        )}
      </div>
    </div>
  );
};

const MetricCell: React.FC<{ metricId: string; spec: MetricSpecOutput | undefined; }> = ({
  metricId,
  spec,
}) => (
  <Tooltip delayDuration={150}>
    <Tooltip.Trigger
      render={
        <span className="font-mono text-xs text-muted-foreground whitespace-nowrap cursor-default hover:text-foreground transition-colors">
          {metricId}
        </span>
      }
    />
    {/* p-0 so inner padding controls the card; text-left neutralizes td inherit */}
    <Tooltip.Content side="bottom" align="start" sideOffset={8} className="p-0 text-left">
      <MetricSpecTooltip metricId={metricId} spec={spec} />
    </Tooltip.Content>
  </Tooltip>
);

const buildSummaryColumns = (
  specByMetricId: ReadonlyMap<string, MetricSpecOutput>,
  alpha: number,
) =>
  createColumns<ExperimentMetricRow>((col) => [
    col.accessor("name", {
      header: "Experiment",
      enableSorting: true,
      enableGlobalFilter: true,
      cell: ({ value, row }) => (
        <Link
          to="/experiments/$id/results"
          params={{ id: row.experiment_id }}
          className={cellStyles.linkCell}
          title={value}
        >
          {value}
        </Link>
      ),
    }),
    col.accessor("exposure_start", {
      header: "Exposure Start",
      enableSorting: true,
      cell: ({ value }) =>
        value
          ? (
            <span className="tabular-nums whitespace-nowrap text-muted-foreground">
              {format(parseISO(value), "MMM d, yyyy")}
            </span>
          )
          : <span className="text-muted-foreground">—</span>,
    }),
    col.accessor("metric_id", {
      header: "Primary Metric",
      enableSorting: true,
      enableGlobalFilter: true,
      cell: ({ value }) => <MetricCell metricId={value} spec={specByMetricId.get(value)} />,
    }),
    col.accessor("n", {
      header: "N",
      enableSorting: true,
      align: "right",
      shrink: true,
      cell: ({ value }) => (
        <span className="tabular-nums whitespace-nowrap text-muted-foreground">
          {formatCount(value)}
        </span>
      ),
    }),
    col("lift", {
      header: "Lift",
      enableSorting: true,
      align: "right",
      shrink: true,
      // Sort on the relative lift; the cell renders the formatted % + color
      // and a hover tooltip with the full contrast breakdown.
      value: (row) => row.contrast.contrast.lift ?? Number.NaN,
      cell: ({ row }) => (
        <MetricLiftCell
          metricId={row.metric_id}
          row={row.contrast}
          spec={specByMetricId.get(row.metric_id)}
          alpha={alpha}
        />
      ),
    }),
    col.accessor("phase", {
      header: "Phase",
      enableSorting: true,
      align: "center",
      shrink: true,
      cell: ({ value }) =>
        value == null
          ? <span className="text-muted-foreground">—</span>
          : <Badge variant={PHASE_VARIANT[value]}>{value}</Badge>,
    }),
  ]);

const PRESET_RANGES = [
  { value: "1M" as const, label: "1m", days: 28 },
  { value: "3M" as const, label: "3m", days: 84 },
  { value: "6M" as const, label: "6m", days: 168 },
  { value: "ALL" as const, label: "All", days: null },
] as const;

type RangePreset = typeof PRESET_RANGES[number]["value"];

// The segmented control's selection: a real preset, or "CUSTOM" once the user
// picks a range straight from the calendar. "CUSTOM" is only ever set
// programmatically — the Custom segment is a state indicator, not an action.
type PresetSelection = RangePreset | "CUSTOM";

const CUSTOM_OPTION = { value: "CUSTOM", label: "Custom" } as const;

// Segmented-control options — presets plus an always-present Custom segment
// that highlights once a calendar-picked range is active.
const PRESET_OPTIONS: ReadonlyArray<{ value: PresetSelection; label: string; }> = [
  ...PRESET_RANGES,
  CUSTOM_OPTION,
];

// Anchor date for the "ALL" preset — earliest assigned_date we expect to find
// in the warehouse. Keeps queries bounded while still spanning all real data.
const ALL_PRESET_START = new Date("2025-01-01T00:00:00");

function buildPresetRange(preset: RangePreset): DateTimeRange {
  const { days } = PRESET_RANGES.find((p) => p.value === preset)!;
  const to = subDays(new Date(), 1); // always relative to yesterday
  const from = days === null ? ALL_PRESET_START : subDays(to, days);
  return { from, to, startTime: "00:00", endTime: "23:59" };
}

const defaultRange = (): DateTimeRange => buildPresetRange("ALL");

const ExperimentSummarySection: React.FC = () => {
  const [preset, setPreset] = React.useState<PresetSelection>("ALL");
  const [range, setRange] = React.useState<DateTimeRange | undefined>(defaultRange);

  const handlePresetChange = (p: PresetSelection) => {
    // Custom is selectable but owns no range — clicking it just marks the
    // selection; the range stays whatever the calendar last set.
    if (p === "CUSTOM") {
      setPreset("CUSTOM");
      return;
    }
    setPreset(p);
    setRange(buildPresetRange(p));
  };

  const handleRangeChange = (r: DateTimeRange | undefined) => {
    setPreset("CUSTOM"); // a calendar-picked range matches no preset
    setRange(r);
  };

  // Only re-fetch when the range is complete. While the user is mid-selection
  // (from set, to undefined), we keep showing the previous result.
  const query = React.useMemo(() => {
    if (!range?.from || !range?.to) return null;
    return { from_date: toISODate(range.from), to_date: toISODate(range.to) };
  }, [range]);

  const summariesQuery = useQuery({
    ...listExperimentMetricsSummaryOptions({ query: query ?? {} }),
    enabled: query !== null,
    // Server always returns a fully-shaped ExperimentMetricsResponse (route
    // wraps _fetcher.load_experiment_summary with an ExperimentStore name
    // map); placeholderData fills the pre-fetch gap with the same shape so
    // downstream code never deals with `undefined`.
    placeholderData: EMPTY_SUMMARY,
    retry: false,
  });

  const summary = summariesQuery.data ?? EMPTY_SUMMARY;
  const metricRows = React.useMemo(
    () => buildExperimentMetricRows(summary.rows, summary.experiments),
    [summary.rows, summary.experiments],
  );

  // Fetch warehouse metric specs so the metric-cell popovers can show
  // description, direction, source table, etc. Warehouse registry is small +
  // rarely changes so the default cache works fine.
  const metricsQuery = useQuery(listWarehouseMetricsOptions());
  const specByMetricId = React.useMemo(() => {
    const map = new Map<string, MetricSpecOutput>();
    for (const spec of metricsQuery.data?.items ?? []) {
      if (spec.metric_id) map.set(spec.metric_id, spec);
    }
    return map;
  }, [metricsQuery.data]);

  const columns = React.useMemo(
    () => buildSummaryColumns(specByMetricId, summary.params.alpha),
    [specByMetricId, summary.params.alpha],
  );

  const grid = useGridClient({
    data: metricRows,
    columns,
    initialSorting: [{ id: "exposure_start", desc: true }],
    getRowId: (row) => `${row.experiment_id}:${row.metric_id}`,
    // `isFetching` covers the initial load AND subsequent refetches after
    // Apply — `isLoading` only fires on the very first fetch.
    ...(summariesQuery.isFetching ? { status: "loading" as const } : {}),
  });

  if (summariesQuery.isError) return null;

  return (
    <DashboardSection
      title="Experiment Summaries"
      tooltip="Lift vs control per metric over the selected assignment window."
    >
      <DataGrid
        {...grid}
        emptyMessage="No experiment summaries for this window."
        className="[&_thead_th]:!h-auto [&_thead_th]:!align-bottom [&_thead_th]:!pt-3 [&_thead_th]:!pb-2 [&_thead_th]:whitespace-nowrap"
        toolbar={
          <div className="flex items-center justify-between gap-4 p-4 border-b">
            <DataGrid.SearchInput
              value={grid.q}
              onSubmit={grid.onSearchSubmit}
              onClear={grid.onSearchClear}
            />
            <div className="flex items-center gap-2 shrink-0">
              <ButtonGroup
                options={PRESET_OPTIONS}
                value={preset}
                onChange={handlePresetChange}
              />
              <DateRangePicker value={range} onChange={handleRangeChange} className="w-[15rem]" />
            </div>
          </div>
        }
      />
    </DashboardSection>
  );
};

const DashboardPage = () => {
  const { data, isLoading } = useQuery(getDashboardStatsOptions());

  return (
    <Page size="full" className="space-y-6">
      <Page.TitleContainer>
        <div>
          <Page.Title>Dashboard</Page.Title>
          <Page.Subtitle>Overview of your workspace</Page.Subtitle>
        </div>
      </Page.TitleContainer>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          label="Active Experiments"
          value={data?.active_experiments}
          icon={FlaskConicalIcon}
          href="/experiments"
          loading={isLoading}
        />
        <StatCard
          label="Active Variants"
          value={data?.active_variants}
          icon={GitBranchIcon}
          href="/experiments"
          loading={isLoading}
        />
        <StatCard
          label="Draft Experiments"
          value={data?.draft_experiments}
          icon={FileEditIcon}
          href="/draft-experiments"
          loading={isLoading}
        />
      </div>

      <ExperimentSummarySection />
    </Page>
  );
};

export const Route = createFileRoute("/")({
  component: DashboardPage,
});
